import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { 
  ArrowRight, Check, Sparkles, Globe, Smartphone, Zap, 
  Palette, Code2, Rocket, Shield, Clock, MessageSquare,
  ChevronRight, Star, Users, TrendingUp, Layout, Layers
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// Local translation helper for component-level translations
const getTranslation = (content, language) => {
  if (content[language]) return content[language];
  if (content['en']) return content['en'];
  if (content['tr']) return content['tr'];
  return content[Object.keys(content)[0]];
};
import TemplateRequestModal from '../components/TemplateRequestModal';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.2 } }
};

// Section wrapper
const Section = ({ children, className = '', id }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      ref={ref}
      id={id}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.section>
  );
};

const WebDesignPage = () => {
  const { language } = useLanguage();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const content = {
    en: {
      badge: 'Web Design & Development',
      headline: 'Your Dream Website,',
      headlineHighlight: 'Built by Experts',
      subheadline: 'We create stunning, high-performance websites that convert visitors into customers. From landing pages to complex web applications.',
      cta: 'Create Free Template',
      ctaSecondary: 'View Pricing',
      stats: [
        { value: '150+', label: 'Websites Delivered' },
        { value: '100%', label: 'Client Satisfaction' },
        { value: '2 Mo', label: 'Free Support' },
        { value: '24/7', label: 'Technical Help' }
      ],
      featuresTitle: 'What We Build',
      features: [
        { icon: 'Layout', title: 'Landing Pages', description: 'High-converting single pages designed to capture leads and drive action.', price: 'From $1,500' },
        { icon: 'Globe', title: 'Corporate Websites', description: 'Professional multi-page websites that establish credibility and trust.', price: 'From $3,000' },
        { icon: 'Smartphone', title: 'E-Commerce', description: 'Full-featured online stores with payment integration and inventory management.', price: 'From $5,000' },
        { icon: 'Code2', title: 'Web Applications', description: 'Custom web apps with databases, user authentication, and complex functionality.', price: 'From $10,000' }
      ],
      processTitle: 'Our Process',
      processSubtitle: 'From concept to launch in 4 simple steps',
      process: [
        { step: '01', title: 'Discovery', description: 'We learn about your business, goals, and target audience.' },
        { step: '02', title: 'Design', description: 'We create stunning mockups and iterate until you love it.' },
        { step: '03', title: 'Development', description: 'Our team builds your site with clean, optimized code.' },
        { step: '04', title: 'Launch', description: 'We deploy, test, and go live.' }
      ],
      includesTitle: 'Every Project Includes',
      includes: ['Mobile-Responsive Design', 'SEO Optimization', 'Speed Optimization', 'SSL Security Certificate', 'Analytics Integration', 'Contact Forms', '2 Months Free Support', 'Training Session'],
      testimonialQuote: '"PIRONE transformed our online presence. Our new website increased leads by 300% in the first month."',
      testimonialAuthor: 'Sarah M.',
      testimonialRole: 'Founder, TechStart',
      ctaTitle: 'Ready to Start?',
      ctaSubtitle: 'Get a free website template tailored to your needs. No commitment required.',
      ctaButton: 'Create Free Template',
      faqTitle: 'Common Questions',
      faqs: [
        { q: 'How long does it take?', a: 'Landing pages: 7-10 days. Corporate sites: 10-15 days. E-commerce: 15-20 days.' },
        { q: 'Do I need to provide content?', a: 'We can help with copywriting and stock images, or use your existing content.' },
        { q: 'What about hosting?', a: 'We can set up hosting for you or work with your existing provider.' },
        { q: 'Can I update the site myself?', a: 'Yes! We build easy-to-manage sites and provide training.' }
      ]
    },
    tr: {
      badge: 'Web Tasarım & Geliştirme',
      headline: 'Hayalinizdeki Web Sitesi,',
      headlineHighlight: 'Uzmanlar Tarafından',
      subheadline: 'Ziyaretçileri müşteriye dönüştüren, çarpıcı ve yüksek performanslı web siteleri oluşturuyoruz. Landing page\'den karmaşık web uygulamalarına kadar.',
      cta: 'Ücretsiz Şablon Oluştur',
      ctaSecondary: 'Fiyatları Gör',
      stats: [
        { value: '150+', label: 'Teslim Edilen Site' },
        { value: '%100', label: 'Müşteri Memnuniyeti' },
        { value: '2 Ay', label: 'Ücretsiz Destek' },
        { value: '7/24', label: 'Teknik Yardım' }
      ],
      featuresTitle: 'Neler Yapıyoruz',
      features: [
        { icon: 'Layout', title: 'Landing Page', description: 'Lead toplayan ve aksiyon aldıran, yüksek dönüşümlü tek sayfa tasarımlar.', price: '$1,500\'dan başlayan' },
        { icon: 'Globe', title: 'Kurumsal Web Sitesi', description: 'Güvenilirlik ve prestij oluşturan profesyonel çok sayfalı siteler.', price: '$3,000\'dan başlayan' },
        { icon: 'Smartphone', title: 'E-Ticaret', description: 'Ödeme entegrasyonu ve stok yönetimi ile tam özellikli online mağazalar.', price: '$5,000\'dan başlayan' },
        { icon: 'Code2', title: 'Web Uygulaması', description: 'Veritabanı, kullanıcı yetkilendirme ve karmaşık işlevsellik içeren özel uygulamalar.', price: '$10,000\'dan başlayan' }
      ],
      processTitle: 'Sürecimiz',
      processSubtitle: 'Konseptten lansmanına 4 basit adım',
      process: [
        { step: '01', title: 'Keşif', description: 'İşinizi, hedeflerinizi ve hedef kitlenizi öğreniyoruz.' },
        { step: '02', title: 'Tasarım', description: 'Çarpıcı mockup\'lar oluşturup beğeninceye kadar geliştiriyoruz.' },
        { step: '03', title: 'Geliştirme', description: 'Ekibimiz sitenizi temiz, optimize kod ile inşa ediyor.' },
        { step: '04', title: 'Lansman', description: 'Yayına alıyor, test ediyor ve canlıya çıkarıyoruz.' }
      ],
      includesTitle: 'Her Projede Dahil',
      includes: ['Mobil Uyumlu Tasarım', 'SEO Optimizasyonu', 'Hız Optimizasyonu', 'SSL Güvenlik Sertifikası', 'Analytics Entegrasyonu', 'İletişim Formları', '2 Ay Ücretsiz Destek', 'Eğitim Oturumu'],
      testimonialQuote: '"PIRONE online varlığımızı dönüştürdü. Yeni web sitemiz ilk ayda lead\'leri %300 artırdı."',
      testimonialAuthor: 'Ahmet K.',
      testimonialRole: 'Kurucu, TechStart',
      ctaTitle: 'Başlamaya Hazır mısınız?',
      ctaSubtitle: 'İhtiyaçlarınıza özel ücretsiz web sitesi şablonu alın. Taahhüt yok.',
      ctaButton: 'Ücretsiz Şablon Oluştur',
      faqTitle: 'Sık Sorulan Sorular',
      faqs: [
        { q: 'Ne kadar sürer?', a: 'Landing page: 7-10 gün. Kurumsal site: 10-15 gün. E-ticaret: 15-20 gün.' },
        { q: 'İçerik sağlamam gerekiyor mu?', a: 'Metin yazımı ve stok görseller konusunda yardımcı olabiliriz veya mevcut içeriğinizi kullanabiliriz.' },
        { q: 'Hosting ne olacak?', a: 'Sizin için hosting ayarlayabiliriz veya mevcut sağlayıcınızla çalışabiliriz.' },
        { q: 'Siteyi kendim güncelleyebilir miyim?', a: 'Evet! Yönetimi kolay siteler yapıyor ve eğitim veriyoruz.' }
      ]
    },
    es: {
      badge: 'Diseño y Desarrollo Web',
      headline: 'Tu Sitio Web Soñado,',
      headlineHighlight: 'Creado por Expertos',
      subheadline: 'Creamos sitios web impresionantes y de alto rendimiento que convierten visitantes en clientes.',
      cta: 'Crear Plantilla Gratis',
      ctaSecondary: 'Ver Precios',
      stats: [
        { value: '150+', label: 'Sitios Entregados' },
        { value: '100%', label: 'Satisfacción' },
        { value: '2 Meses', label: 'Soporte Gratis' },
        { value: '24/7', label: 'Ayuda Técnica' }
      ],
      featuresTitle: 'Lo Que Construimos',
      features: [
        { icon: 'Layout', title: 'Landing Pages', description: 'Páginas de alta conversión diseñadas para capturar leads.', price: 'Desde $1,500' },
        { icon: 'Globe', title: 'Sitios Corporativos', description: 'Sitios web profesionales que establecen credibilidad.', price: 'Desde $3,000' },
        { icon: 'Smartphone', title: 'E-Commerce', description: 'Tiendas online con integración de pagos.', price: 'Desde $5,000' },
        { icon: 'Code2', title: 'Aplicaciones Web', description: 'Apps personalizadas con bases de datos y autenticación.', price: 'Desde $10,000' }
      ],
      processTitle: 'Nuestro Proceso',
      processSubtitle: 'Del concepto al lanzamiento en 4 pasos',
      process: [
        { step: '01', title: 'Descubrimiento', description: 'Conocemos tu negocio y objetivos.' },
        { step: '02', title: 'Diseño', description: 'Creamos mockups hasta que te encante.' },
        { step: '03', title: 'Desarrollo', description: 'Construimos con código optimizado.' },
        { step: '04', title: 'Lanzamiento', description: 'Desplegamos, probamos y lanzamos.' }
      ],
      includesTitle: 'Cada Proyecto Incluye',
      includes: ['Diseño Responsive', 'Optimización SEO', 'Optimización de Velocidad', 'Certificado SSL', 'Integración Analytics', 'Formularios', '2 Meses Soporte', 'Capacitación'],
      testimonialQuote: '"PIRONE transformó nuestra presencia online. Los leads aumentaron 300%."',
      testimonialAuthor: 'María G.',
      testimonialRole: 'Fundadora, TechStart',
      ctaTitle: '¿Listo para Empezar?',
      ctaSubtitle: 'Obtén una plantilla web gratuita. Sin compromiso.',
      ctaButton: 'Crear Plantilla Gratis',
      faqTitle: 'Preguntas Frecuentes',
      faqs: [
        { q: '¿Cuánto tiempo toma?', a: 'Landing: 7-10 días. Corporativo: 10-15 días. E-commerce: 15-20 días.' },
        { q: '¿Necesito proveer contenido?', a: 'Podemos ayudar con textos e imágenes.' },
        { q: '¿Qué hay del hosting?', a: 'Lo configuramos o trabajamos con tu proveedor.' },
        { q: '¿Puedo actualizar el sitio?', a: '¡Sí! Construimos sitios fáciles de gestionar.' }
      ]
    },
    pt: {
      badge: 'Design e Desenvolvimento Web',
      headline: 'Seu Site dos Sonhos,',
      headlineHighlight: 'Criado por Especialistas',
      subheadline: 'Criamos sites impressionantes e de alto desempenho que convertem visitantes em clientes.',
      cta: 'Criar Template Grátis',
      ctaSecondary: 'Ver Preços',
      stats: [
        { value: '150+', label: 'Sites Entregues' },
        { value: '100%', label: 'Satisfação' },
        { value: '2 Meses', label: 'Suporte Grátis' },
        { value: '24/7', label: 'Ajuda Técnica' }
      ],
      featuresTitle: 'O Que Construímos',
      features: [
        { icon: 'Layout', title: 'Landing Pages', description: 'Páginas de alta conversão para capturar leads.', price: 'A partir de $1,500' },
        { icon: 'Globe', title: 'Sites Corporativos', description: 'Sites profissionais que estabelecem credibilidade.', price: 'A partir de $3,000' },
        { icon: 'Smartphone', title: 'E-Commerce', description: 'Lojas online com integração de pagamentos.', price: 'A partir de $5,000' },
        { icon: 'Code2', title: 'Aplicações Web', description: 'Apps personalizados com banco de dados e autenticação.', price: 'A partir de $10,000' }
      ],
      processTitle: 'Nosso Processo',
      processSubtitle: 'Do conceito ao lançamento em 4 passos',
      process: [
        { step: '01', title: 'Descoberta', description: 'Conhecemos seu negócio e objetivos.' },
        { step: '02', title: 'Design', description: 'Criamos mockups até você amar.' },
        { step: '03', title: 'Desenvolvimento', description: 'Construímos com código otimizado.' },
        { step: '04', title: 'Lançamento', description: 'Implantamos, testamos e lançamos.' }
      ],
      includesTitle: 'Todo Projeto Inclui',
      includes: ['Design Responsivo', 'Otimização SEO', 'Otimização de Velocidade', 'Certificado SSL', 'Integração Analytics', 'Formulários', '2 Meses Suporte', 'Treinamento'],
      testimonialQuote: '"PIRONE transformou nossa presença online. Os leads aumentaram 300%."',
      testimonialAuthor: 'João S.',
      testimonialRole: 'Fundador, TechStart',
      ctaTitle: 'Pronto para Começar?',
      ctaSubtitle: 'Obtenha um template web gratuito. Sem compromisso.',
      ctaButton: 'Criar Template Grátis',
      faqTitle: 'Perguntas Frequentes',
      faqs: [
        { q: 'Quanto tempo leva?', a: 'Landing: 7-10 dias. Corporativo: 10-15 dias. E-commerce: 15-20 dias.' },
        { q: 'Preciso fornecer conteúdo?', a: 'Podemos ajudar com textos e imagens.' },
        { q: 'E o hosting?', a: 'Configuramos ou trabalhamos com seu provedor.' },
        { q: 'Posso atualizar o site?', a: 'Sim! Construímos sites fáceis de gerenciar.' }
      ]
    },
    hi: {
      badge: 'वेब डिज़ाइन और विकास',
      headline: 'आपकी सपनों की वेबसाइट,',
      headlineHighlight: 'विशेषज्ञों द्वारा निर्मित',
      subheadline: 'हम शानदार, उच्च-प्रदर्शन वेबसाइट बनाते हैं जो विज़िटर को ग्राहकों में बदलती हैं।',
      cta: 'मुफ्त टेम्पलेट बनाएं',
      ctaSecondary: 'कीमतें देखें',
      stats: [
        { value: '150+', label: 'वेबसाइट डिलीवर' },
        { value: '100%', label: 'संतुष्टि' },
        { value: '2 महीने', label: 'मुफ्त सपोर्ट' },
        { value: '24/7', label: 'तकनीकी मदद' }
      ],
      featuresTitle: 'हम क्या बनाते हैं',
      features: [
        { icon: 'Layout', title: 'लैंडिंग पेज', description: 'लीड कैप्चर करने के लिए उच्च-रूपांतरण पेज।', price: '$1,500 से' },
        { icon: 'Globe', title: 'कॉर्पोरेट वेबसाइट', description: 'विश्वसनीयता स्थापित करने वाली पेशेवर वेबसाइट।', price: '$3,000 से' },
        { icon: 'Smartphone', title: 'ई-कॉमर्स', description: 'भुगतान एकीकरण के साथ ऑनलाइन स्टोर।', price: '$5,000 से' },
        { icon: 'Code2', title: 'वेब एप्लिकेशन', description: 'डेटाबेस और प्रमाणीकरण के साथ कस्टम ऐप।', price: '$10,000 से' }
      ],
      processTitle: 'हमारी प्रक्रिया',
      processSubtitle: 'कॉन्सेप्ट से लॉन्च तक 4 चरणों में',
      process: [
        { step: '01', title: 'खोज', description: 'आपके व्यवसाय और लक्ष्यों को समझना।' },
        { step: '02', title: 'डिज़ाइन', description: 'शानदार मॉकअप बनाना।' },
        { step: '03', title: 'विकास', description: 'अनुकूलित कोड के साथ निर्माण।' },
        { step: '04', title: 'लॉन्च', description: 'हम तैनात, परीक्षण और लॉन्च करते हैं।' }
      ],
      includesTitle: 'हर प्रोजेक्ट में शामिल',
      includes: ['मोबाइल-रेस्पॉन्सिव', 'SEO ऑप्टिमाइज़ेशन', 'स्पीड ऑप्टिमाइज़ेशन', 'SSL सर्टिफिकेट', 'Analytics', 'फॉर्म', '2 महीने सपोर्ट', 'ट्रेनिंग'],
      testimonialQuote: '"PIRONE ने हमारी ऑनलाइन उपस्थिति बदल दी। लीड 300% बढ़े।"',
      testimonialAuthor: 'राहुल के.',
      testimonialRole: 'संस्थापक, TechStart',
      ctaTitle: 'शुरू करने के लिए तैयार?',
      ctaSubtitle: 'मुफ्त वेबसाइट टेम्पलेट प्राप्त करें। कोई प्रतिबद्धता नहीं।',
      ctaButton: 'मुफ्त टेम्पलेट बनाएं',
      faqTitle: 'अक्सर पूछे जाने वाले प्रश्न',
      faqs: [
        { q: 'कितना समय लगता है?', a: 'लैंडिंग: 7-10 दिन। कॉर्पोरेट: 10-15 दिन। ई-कॉमर्स: 15-20 दिन।' },
        { q: 'क्या मुझे सामग्री देनी होगी?', a: 'हम टेक्स्ट और इमेज में मदद कर सकते हैं।' },
        { q: 'होस्टिंग का क्या?', a: 'हम सेटअप करते हैं या आपके प्रोवाइडर के साथ काम करते हैं।' },
        { q: 'क्या मैं साइट अपडेट कर सकता हूं?', a: 'हां! हम आसान साइट बनाते हैं।' }
      ]
    },
    ko: {
      badge: '웹 디자인 & 개발',
      headline: '꿈의 웹사이트,',
      headlineHighlight: '전문가가 제작',
      subheadline: '방문자를 고객으로 전환하는 멋진 고성능 웹사이트를 만듭니다.',
      cta: '무료 템플릿 만들기',
      ctaSecondary: '가격 보기',
      stats: [
        { value: '150+', label: '납품된 사이트' },
        { value: '100%', label: '만족도' },
        { value: '2개월', label: '무료 지원' },
        { value: '24/7', label: '기술 지원' }
      ],
      featuresTitle: '우리가 만드는 것',
      features: [
        { icon: 'Layout', title: '랜딩 페이지', description: '리드를 캡처하는 고전환 페이지.', price: '$1,500부터' },
        { icon: 'Globe', title: '기업 웹사이트', description: '신뢰성을 확립하는 전문 웹사이트.', price: '$3,000부터' },
        { icon: 'Smartphone', title: '이커머스', description: '결제 통합이 있는 온라인 스토어.', price: '$5,000부터' },
        { icon: 'Code2', title: '웹 애플리케이션', description: '데이터베이스와 인증이 있는 맞춤 앱.', price: '$10,000부터' }
      ],
      processTitle: '우리의 프로세스',
      processSubtitle: '컨셉부터 런칭까지 4단계',
      process: [
        { step: '01', title: '발견', description: '비즈니스와 목표 파악.' },
        { step: '02', title: '디자인', description: '멋진 목업 제작.' },
        { step: '03', title: '개발', description: '최적화된 코드로 구축.' },
        { step: '04', title: '런칭', description: '배포, 테스트 및 런칭.' }
      ],
      includesTitle: '모든 프로젝트 포함',
      includes: ['반응형 디자인', 'SEO 최적화', '속도 최적화', 'SSL 인증서', '분석 통합', '연락 양식', '2개월 지원', '교육'],
      testimonialQuote: '"PIRONE이 온라인 존재감을 바꿨습니다. 리드가 300% 증가했습니다."',
      testimonialAuthor: '김민수',
      testimonialRole: '창업자, TechStart',
      ctaTitle: '시작할 준비되셨나요?',
      ctaSubtitle: '무료 웹사이트 템플릿을 받으세요. 약정 없음.',
      ctaButton: '무료 템플릿 만들기',
      faqTitle: '자주 묻는 질문',
      faqs: [
        { q: '얼마나 걸리나요?', a: '랜딩: 7-10일. 기업: 10-15일. 이커머스: 15-20일.' },
        { q: '콘텐츠를 제공해야 하나요?', a: '텍스트와 이미지를 도와드립니다.' },
        { q: '호스팅은요?', a: '설정해 드리거나 기존 제공업체와 협력합니다.' },
        { q: '사이트를 직접 업데이트할 수 있나요?', a: '네! 관리하기 쉬운 사이트를 만듭니다.' }
      ]
    },
    zh: {
      badge: '网站设计与开发',
      headline: '您梦想中的网站，',
      headlineHighlight: '由专家打造',
      subheadline: '我们创建令人惊叹的高性能网站，将访客转化为客户。',
      cta: '创建免费模板',
      ctaSecondary: '查看价格',
      stats: [
        { value: '150+', label: '已交付网站' },
        { value: '100%', label: '满意度' },
        { value: '2个月', label: '免费支持' },
        { value: '24/7', label: '技术支持' }
      ],
      featuresTitle: '我们构建什么',
      features: [
        { icon: 'Layout', title: '着陆页', description: '高转化率的单页设计。', price: '起价 $1,500' },
        { icon: 'Globe', title: '企业网站', description: '建立信誉的专业网站。', price: '起价 $3,000' },
        { icon: 'Smartphone', title: '电子商务', description: '带支付集成的在线商店。', price: '起价 $5,000' },
        { icon: 'Code2', title: '网络应用', description: '带数据库和认证的定制应用。', price: '起价 $10,000' }
      ],
      processTitle: '我们的流程',
      processSubtitle: '从概念到上线只需4步',
      process: [
        { step: '01', title: '发现', description: '了解您的业务和目标。' },
        { step: '02', title: '设计', description: '创建精美的设计稿。' },
        { step: '03', title: '开发', description: '用优化的代码构建。' },
        { step: '04', title: '上线', description: '部署、测试并上线。' }
      ],
      includesTitle: '每个项目包含',
      includes: ['响应式设计', 'SEO优化', '速度优化', 'SSL证书', '分析集成', '联系表单', '2个月支持', '培训'],
      testimonialQuote: '"PIRONE改变了我们的在线形象。潜在客户增加了300%。"',
      testimonialAuthor: '王明',
      testimonialRole: '创始人, TechStart',
      ctaTitle: '准备开始了吗？',
      ctaSubtitle: '获取免费网站模板。无需承诺。',
      ctaButton: '创建免费模板',
      faqTitle: '常见问题',
      faqs: [
        { q: '需要多长时间？', a: '着陆页: 7-10天。企业: 10-15天。电商: 15-20天。' },
        { q: '我需要提供内容吗？', a: '我们可以帮助提供文案和图片。' },
        { q: '托管怎么办？', a: '我们可以设置或与您的供应商合作。' },
        { q: '我可以自己更新网站吗？', a: '可以！我们构建易于管理的网站。' }
      ]
    },
    el: {
      badge: 'Σχεδιασμός & Ανάπτυξη Ιστοσελίδων',
      headline: 'Η Ιστοσελίδα των Ονείρων σας,',
      headlineHighlight: 'Από Ειδικούς',
      subheadline: 'Δημιουργούμε εκπληκτικές ιστοσελίδες υψηλής απόδοσης που μετατρέπουν επισκέπτες σε πελάτες.',
      cta: 'Δημιουργία Δωρεάν Προτύπου',
      ctaSecondary: 'Δείτε Τιμές',
      stats: [
        { value: '150+', label: 'Ιστοσελίδες' },
        { value: '100%', label: 'Ικανοποίηση' },
        { value: '2 Μήνες', label: 'Δωρεάν Υποστήριξη' },
        { value: '24/7', label: 'Τεχνική Βοήθεια' }
      ],
      featuresTitle: 'Τι Δημιουργούμε',
      features: [
        { icon: 'Layout', title: 'Landing Pages', description: 'Σελίδες υψηλής μετατροπής.', price: 'Από $1,500' },
        { icon: 'Globe', title: 'Εταιρικές Ιστοσελίδες', description: 'Επαγγελματικές ιστοσελίδες.', price: 'Από $3,000' },
        { icon: 'Smartphone', title: 'E-Commerce', description: 'Online καταστήματα με πληρωμές.', price: 'Από $5,000' },
        { icon: 'Code2', title: 'Web Εφαρμογές', description: 'Προσαρμοσμένες εφαρμογές.', price: 'Από $10,000' }
      ],
      processTitle: 'Η Διαδικασία μας',
      processSubtitle: 'Από την ιδέα στην κυκλοφορία σε 4 βήματα',
      process: [
        { step: '01', title: 'Ανακάλυψη', description: 'Μαθαίνουμε για την επιχείρησή σας.' },
        { step: '02', title: 'Σχεδιασμός', description: 'Δημιουργούμε mockups.' },
        { step: '03', title: 'Ανάπτυξη', description: 'Κατασκευή με βελτιστοποιημένο κώδικα.' },
        { step: '04', title: 'Κυκλοφορία', description: 'Ανάπτυξη, δοκιμή και κυκλοφορία.' }
      ],
      includesTitle: 'Κάθε Έργο Περιλαμβάνει',
      includes: ['Responsive Design', 'SEO', 'Ταχύτητα', 'SSL', 'Analytics', 'Φόρμες', '2 Μήνες Υποστήριξη', 'Εκπαίδευση'],
      testimonialQuote: '"Το PIRONE άλλαξε την online παρουσία μας. Τα leads αυξήθηκαν 300%."',
      testimonialAuthor: 'Γιώργος Κ.',
      testimonialRole: 'Ιδρυτής, TechStart',
      ctaTitle: 'Έτοιμοι να Ξεκινήσετε;',
      ctaSubtitle: 'Λάβετε δωρεάν πρότυπο. Χωρίς δέσμευση.',
      ctaButton: 'Δημιουργία Δωρεάν Προτύπου',
      faqTitle: 'Συχνές Ερωτήσεις',
      faqs: [
        { q: 'Πόσο χρόνο χρειάζεται;', a: 'Landing: 7-10 μέρες. Εταιρικό: 10-15 μέρες. E-commerce: 15-20 μέρες.' },
        { q: 'Χρειάζεται να δώσω περιεχόμενο;', a: 'Μπορούμε να βοηθήσουμε με κείμενα και εικόνες.' },
        { q: 'Τι γίνεται με το hosting;', a: 'Το ρυθμίζουμε ή συνεργαζόμαστε με τον πάροχό σας.' },
        { q: 'Μπορώ να ενημερώσω τη σελίδα;', a: 'Ναι! Φτιάχνουμε εύκολες στη διαχείριση σελίδες.' }
      ]
    },
    ar: {
      badge: 'تصميم وتطوير المواقع',
      headline: 'موقعك المثالي،',
      headlineHighlight: 'من الخبراء',
      subheadline: 'نصمم مواقع مذهلة عالية الأداء تحول الزوار إلى عملاء.',
      cta: 'إنشاء قالب مجاني',
      ctaSecondary: 'عرض الأسعار',
      stats: [
        { value: '+150', label: 'موقع منجز' },
        { value: '100%', label: 'رضا العملاء' },
        { value: 'شهرين', label: 'دعم مجاني' },
        { value: '24/7', label: 'مساعدة تقنية' }
      ],
      featuresTitle: 'ما نبنيه',
      features: [
        { icon: 'Layout', title: 'صفحات الهبوط', description: 'صفحات عالية التحويل.', price: 'من $1,500' },
        { icon: 'Globe', title: 'مواقع الشركات', description: 'مواقع احترافية.', price: 'من $3,000' },
        { icon: 'Smartphone', title: 'التجارة الإلكترونية', description: 'متاجر بتكامل الدفع.', price: 'من $5,000' },
        { icon: 'Code2', title: 'تطبيقات الويب', description: 'تطبيقات مخصصة.', price: 'من $10,000' }
      ],
      processTitle: 'عمليتنا',
      processSubtitle: 'من الفكرة للإطلاق في 4 خطوات',
      process: [
        { step: '01', title: 'الاكتشاف', description: 'نتعرف على عملك وأهدافك.' },
        { step: '02', title: 'التصميم', description: 'نصمم نماذج مذهلة.' },
        { step: '03', title: 'التطوير', description: 'نبني بكود محسن.' },
        { step: '04', title: 'الإطلاق', description: 'ننشر ونختبر ونطلق.' }
      ],
      includesTitle: 'كل مشروع يشمل',
      includes: ['تصميم متجاوب', 'تحسين SEO', 'تحسين السرعة', 'شهادة SSL', 'Analytics', 'نماذج', 'شهرين دعم', 'تدريب'],
      testimonialQuote: '"PIRONE غير وجودنا الرقمي. زادت العملاء المحتملين 300%."',
      testimonialAuthor: 'أحمد م.',
      testimonialRole: 'مؤسس، TechStart',
      ctaTitle: 'جاهز للبدء؟',
      ctaSubtitle: 'احصل على قالب موقع مجاني. بدون التزام.',
      ctaButton: 'إنشاء قالب مجاني',
      faqTitle: 'أسئلة شائعة',
      faqs: [
        { q: 'كم يستغرق الوقت؟', a: 'صفحة هبوط: 7-10 أيام. شركات: 10-15 يوم. تجارة: 15-20 يوم.' },
        { q: 'هل أحتاج توفير محتوى؟', a: 'يمكننا المساعدة بالنصوص والصور.' },
        { q: 'ماذا عن الاستضافة؟', a: 'نقوم بالإعداد أو نعمل مع مزودك.' },
        { q: 'هل يمكنني تحديث الموقع؟', a: 'نعم! نبني مواقع سهلة الإدارة.' }
      ]
    },
    it: {
      badge: 'Design e Sviluppo Web',
      headline: 'Il Tuo Sito Web Ideale,',
      headlineHighlight: 'Creato da Esperti',
      subheadline: 'Creiamo siti web straordinari e ad alte prestazioni che convertono i visitatori in clienti.',
      cta: 'Crea Template Gratis',
      ctaSecondary: 'Vedi Prezzi',
      stats: [
        { value: '150+', label: 'Siti Consegnati' },
        { value: '100%', label: 'Soddisfazione' },
        { value: '2 Mesi', label: 'Supporto Gratis' },
        { value: '24/7', label: 'Assistenza' }
      ],
      featuresTitle: 'Cosa Costruiamo',
      features: [
        { icon: 'Layout', title: 'Landing Page', description: 'Pagine ad alta conversione.', price: 'Da $1,500' },
        { icon: 'Globe', title: 'Siti Aziendali', description: 'Siti professionali che creano credibilità.', price: 'Da $3,000' },
        { icon: 'Smartphone', title: 'E-Commerce', description: 'Negozi online con integrazione pagamenti.', price: 'Da $5,000' },
        { icon: 'Code2', title: 'Applicazioni Web', description: 'App personalizzate con database.', price: 'Da $10,000' }
      ],
      processTitle: 'Il Nostro Processo',
      processSubtitle: 'Dal concetto al lancio in 4 passi',
      process: [
        { step: '01', title: 'Scoperta', description: 'Conosciamo il tuo business.' },
        { step: '02', title: 'Design', description: 'Creiamo mockup straordinari.' },
        { step: '03', title: 'Sviluppo', description: 'Costruiamo con codice ottimizzato.' },
        { step: '04', title: 'Lancio', description: 'Distribuiamo, testiamo e lanciamo.' }
      ],
      includesTitle: 'Ogni Progetto Include',
      includes: ['Design Responsive', 'Ottimizzazione SEO', 'Velocità', 'Certificato SSL', 'Analytics', 'Moduli Contatto', '2 Mesi Supporto', 'Formazione'],
      testimonialQuote: '"PIRONE ha trasformato la nostra presenza online. I lead sono aumentati del 300%."',
      testimonialAuthor: 'Marco R.',
      testimonialRole: 'Fondatore, TechStart',
      ctaTitle: 'Pronto a Iniziare?',
      ctaSubtitle: 'Ottieni un template web gratuito. Nessun impegno.',
      ctaButton: 'Crea Template Gratis',
      faqTitle: 'Domande Frequenti',
      faqs: [
        { q: 'Quanto tempo ci vuole?', a: 'Landing: 7-10 giorni. Aziendale: 10-15 giorni. E-commerce: 15-20 giorni.' },
        { q: 'Devo fornire contenuti?', a: 'Possiamo aiutare con testi e immagini.' },
        { q: 'E l\'hosting?', a: 'Lo configuriamo noi o lavoriamo col tuo provider.' },
        { q: 'Posso aggiornare il sito?', a: 'Sì! Costruiamo siti facili da gestire.' }
      ]
    },
    gu: {
      badge: 'વેબ ડિઝાઇન અને વિકાસ',
      headline: 'તમારી સ્વપ્નની વેબસાઇટ,',
      headlineHighlight: 'નિષ્ણાતો દ્વારા',
      subheadline: 'અમે શાનદાર, ઉચ્ચ-પ્રદર્શન વેબસાઇટ્સ બનાવીએ છીએ જે મુલાકાતીઓને ગ્રાહકોમાં રૂપાંતરિત કરે છે.',
      cta: 'મફત ટેમ્પલેટ બનાવો',
      ctaSecondary: 'કિંમતો જુઓ',
      stats: [
        { value: '150+', label: 'વેબસાઇટ્સ' },
        { value: '100%', label: 'સંતોષ' },
        { value: '2 મહિના', label: 'મફત સપોર્ટ' },
        { value: '24/7', label: 'ટેકનિકલ મદદ' }
      ],
      featuresTitle: 'અમે શું બનાવીએ છીએ',
      features: [
        { icon: 'Layout', title: 'લેન્ડિંગ પેજ', description: 'ઉચ્ચ રૂપાંતરણ પેજ.', price: '$1,500 થી' },
        { icon: 'Globe', title: 'કોર્પોરેટ વેબસાઇટ', description: 'વ્યાવસાયિક વેબસાઇટ્સ.', price: '$3,000 થી' },
        { icon: 'Smartphone', title: 'ઇ-કોમર્સ', description: 'પેમેન્ટ સાથે ઓનલાઇન સ્ટોર.', price: '$5,000 થી' },
        { icon: 'Code2', title: 'વેબ એપ્લિકેશન', description: 'કસ્ટમ એપ્સ.', price: '$10,000 થી' }
      ],
      processTitle: 'અમારી પ્રક્રિયા',
      processSubtitle: 'કોન્સેપ્ટથી લોન્ચ સુધી 4 પગલાંમાં',
      process: [
        { step: '01', title: 'શોધ', description: 'તમારા વ્યવસાયને સમજવું.' },
        { step: '02', title: 'ડિઝાઇન', description: 'મોકઅપ્સ બનાવવા.' },
        { step: '03', title: 'વિકાસ', description: 'ઓપ્ટિમાઇઝ્ડ કોડ સાથે બિલ્ડ.' },
        { step: '04', title: 'લોન્ચ', description: 'અમે તૈનાત, પરીક્ષણ અને લોન્ચ કરીએ છીએ.' }
      ],
      includesTitle: 'દરેક પ્રોજેક્ટમાં સામેલ',
      includes: ['રિસ્પોન્સિવ ડિઝાઇન', 'SEO', 'સ્પીડ', 'SSL', 'Analytics', 'ફોર્મ્સ', '2 મહિના સપોર્ટ', 'ટ્રેનિંગ'],
      testimonialQuote: '"PIRONE એ અમારી ઓનલાઇન હાજરી બદલી. લીડ્સ 300% વધ્યા."',
      testimonialAuthor: 'રાજેશ પ.',
      testimonialRole: 'સ્થાપક, TechStart',
      ctaTitle: 'શરૂ કરવા તૈયાર?',
      ctaSubtitle: 'મફત વેબસાઇટ ટેમ્પલેટ મેળવો. કોઈ પ્રતિબદ્ધતા નહીં.',
      ctaButton: 'મફત ટેમ્પલેટ બનાવો',
      faqTitle: 'વારંવાર પૂછાતા પ્રશ્નો',
      faqs: [
        { q: 'કેટલો સમય લાગે?', a: 'લેન્ડિંગ: 7-10 દિવસ. કોર્પોરેટ: 10-15 દિવસ. ઇ-કોમર્સ: 15-20 દિવસ.' },
        { q: 'શું મારે કન્ટેન્ટ આપવું પડશે?', a: 'અમે ટેક્સ્ટ અને ઇમેજમાં મદદ કરી શકીએ.' },
        { q: 'હોસ્ટિંગનું શું?', a: 'અમે સેટઅપ કરીએ અથવા તમારા પ્રોવાઇડર સાથે કામ કરીએ.' },
        { q: 'શું હું સાઇટ અપડેટ કરી શકું?', a: 'હા! અમે સરળ સાઇટ્સ બનાવીએ છીએ.' }
      ]
    }
  };

  const t = getTranslation(content, language);

  const getIcon = (iconName) => {
    const icons = {
      Layout: <Layout size={24} />,
      Globe: <Globe size={24} />,
      Smartphone: <Smartphone size={24} />,
      Code2: <Code2 size={24} />
    };
    return icons[iconName] || <Sparkles size={24} />;
  };

  return (
    <div className="min-h-screen bg-transparent pt-24 relative overflow-hidden">
      {/* Background Grid */}
      <div className="fixed inset-0 opacity-[0.02] pointer-events-none">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="webGrid" width="60" height="60" patternUnits="userSpaceOnUse">
              <path d="M 60 0 L 0 0 0 60" fill="none" stroke="white" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#webGrid)" />
        </svg>
      </div>

      {/* Floating Orbs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-[#38BDF8]/5 rounded-full blur-[100px]" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-cyan-500/5 rounded-full blur-[100px]" />
      </div>

      {/* HERO SECTION */}
      <Section className="relative z-10 py-20 lg:py-32">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#38BDF8]/10 border border-[#38BDF8]/20 mb-8">
              <Code2 size={14} className="text-[#38BDF8]" />
              <span className="text-[#38BDF8] text-xs font-medium tracking-wide">{t.badge}</span>
            </motion.div>

            <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 leading-[1.1]">
              {t.headline}
              <br />
              <span className="bg-gradient-to-r from-[#38BDF8] to-cyan-400 bg-clip-text text-transparent">
                {t.headlineHighlight}
              </span>
            </motion.h1>

            <motion.p variants={fadeInUp} className="text-lg sm:text-xl text-white/50 mb-10 max-w-2xl mx-auto leading-relaxed">
              {t.subheadline}
            </motion.p>

            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => setIsModalOpen(true)}
                className="group flex items-center gap-3 px-8 py-4 bg-[#38BDF8] text-black font-semibold rounded-xl hover:bg-white transition-all duration-300"
                data-testid="web-design-cta"
              >
                <span>{t.cta}</span>
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <a href="/contact" className="flex items-center gap-2 px-8 py-4 text-white/60 hover:text-white transition-colors">
                <span>{t.ctaSecondary}</span>
                <ChevronRight size={16} />
              </a>
            </motion.div>
          </div>

          <motion.div variants={fadeInUp} className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6">
            {t.stats.filter((_, i) => i !== 2).map((stat, i) => (
              <div key={i} className="text-center p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
                <div className="text-3xl lg:text-4xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-sm text-white/40">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </Section>

      {/* FEATURES SECTION */}
      <Section className="relative z-10 py-20">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">{t.featuresTitle}</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {t.features.map((feature, i) => (
              <motion.div key={i} variants={fadeInUp} className="group p-8 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-[#38BDF8]/30 transition-all duration-500">
                <div className="flex items-start gap-5">
                  <div className="w-14 h-14 rounded-xl bg-[#38BDF8]/10 border border-[#38BDF8]/20 flex items-center justify-center text-[#38BDF8] flex-shrink-0">
                    {getIcon(feature.icon)}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                    <p className="text-white/50 text-sm leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* PROCESS SECTION */}
      <Section className="relative z-10 py-20">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">{t.processTitle}</h2>
            <p className="text-white/50">{t.processSubtitle}</p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {t.process.map((step, i) => (
              <motion.div key={i} variants={fadeInUp} className="relative">
                {i < 3 && <div className="hidden md:block absolute top-8 left-[calc(50%+40px)] w-[calc(100%-80px)] h-[2px] bg-gradient-to-r from-[#38BDF8]/30 to-transparent" />}
                <div className="text-center p-6">
                  <div className="w-16 h-16 rounded-2xl bg-[#38BDF8]/10 border border-[#38BDF8]/20 flex items-center justify-center mx-auto mb-4">
                    <span className="text-[#38BDF8] font-bold text-lg">{step.step}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
                  <p className="text-white/40 text-sm">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* INCLUDES SECTION */}
      <Section className="relative z-10 py-20">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div variants={fadeInUp}>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-8">{t.includesTitle}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {t.includes.filter((_, i) => i !== 6).map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#38BDF8]/20 flex items-center justify-center flex-shrink-0">
                      <Check size={14} className="text-[#38BDF8]" />
                    </div>
                    <span className="text-white/70 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="p-8 rounded-2xl bg-gradient-to-br from-[#38BDF8]/10 to-cyan-500/5 border border-[#38BDF8]/20">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => <Star key={i} size={16} className="fill-[#38BDF8] text-[#38BDF8]" />)}
              </div>
              <p className="text-white/80 text-lg italic mb-6 leading-relaxed">{t.testimonialQuote}</p>
              <div>
                <p className="text-white font-semibold">{t.testimonialAuthor}</p>
                <p className="text-white/40 text-sm">{t.testimonialRole}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </Section>

      {/* FAQ SECTION */}
      <Section className="relative z-10 py-20">
        <div className="max-w-[800px] mx-auto px-6 lg:px-12">
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white">{t.faqTitle}</h2>
          </motion.div>

          <div className="space-y-4">
            {t.faqs.map((faq, i) => (
              <motion.div key={i} variants={fadeInUp} className="p-6 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                <h3 className="text-white font-medium mb-2">{faq.q}</h3>
                <p className="text-white/50 text-sm">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* FINAL CTA SECTION */}
      <Section className="relative z-10 py-20 mb-20">
        <div className="max-w-[800px] mx-auto px-6 lg:px-12">
          <motion.div variants={fadeInUp} className="text-center p-12 rounded-3xl bg-gradient-to-br from-[#38BDF8]/10 to-cyan-500/5 border border-[#38BDF8]/20">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">{t.ctaTitle}</h2>
            <p className="text-white/50 mb-8">{t.ctaSubtitle}</p>
            <button
              onClick={() => setIsModalOpen(true)}
              className="group inline-flex items-center gap-3 px-10 py-5 bg-[#38BDF8] text-black font-semibold rounded-xl hover:bg-white transition-all duration-300"
              data-testid="web-design-final-cta"
            >
              <span>{t.ctaButton}</span>
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>
        </div>
      </Section>

      <TemplateRequestModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default WebDesignPage;
